import type { FieldState, VisibilityConfig } from '@/types/FieldConfig'

export interface StateOption {
  value: FieldState;
  /** Untranslated, so the caller decides where the string is rendered. */
  label: string;
  disabled?: boolean;
}

export const stateLabels: Record<FieldState, string> = {
  hidden: 'Hidden',
  optional: 'Optional',
  required: 'Required',
}

/** How loudly a place asks for the field, not whether the setting is good. */
export const stateVariants: Record<FieldState, 'muted' | 'neutral' | 'accent'> = {
  hidden: 'muted',
  optional: 'neutral',
  required: 'accent',
}

/**
 * A locked choice stays visible rather than disappearing, so the reason it cannot
 * be made is legible instead of the option simply being absent.
 *
 * A choice is locked when making it would change what the field locks, from where
 * the place stands now: a field whose requirement is locked can still be shown or
 * hidden, as long as that leaves its requirement as it is.
 */
export const stateOptions = (locked?: VisibilityConfig, current: FieldState = 'hidden'): StateOption[] => {
  const showing = current === 'hidden' && !!locked?.display

  const options: StateOption[] = [
    { value: 'hidden', label: stateLabels.hidden, disabled: current !== 'hidden' && !!locked?.display },
    { value: 'optional', label: stateLabels.optional, disabled: showing || (current === 'required' && !!locked?.required) },
    { value: 'required', label: stateLabels.required, disabled: showing || (current !== 'required' && !!locked?.required) },
  ]

  // Nothing else can be picked: the place reads as locked as a whole, its own state included.
  return options.every(option => option.disabled || option.value === current)
    ? options.map(option => ({ ...option, disabled: true }))
    : options
}

/** A place where nothing but its current state can be picked cannot be edited at all. */
export const isLocked = (locked?: VisibilityConfig, current: FieldState = 'hidden'): boolean =>
  stateOptions(locked, current).every(option => option.disabled)
